"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/overlays/dropdown-menu";
import { ChevronDown, X } from "lucide-react";
import { StripePaymentWidget } from "@/lib/payment-widgets/StripePaymentWidget";
import { YooKassaPaymentWidget } from "@/lib/payment-widgets/YooKassaPaymentWidget";
import { TiptopPaymentWidget } from "@/lib/payment-widgets/TiptopPaymentWidget";
import { authService } from "@/lib/services/auth.service";
import { YMTracker } from "@/lib/services/ym-tracker.service";
import { useBalance } from "@/contexts/BalanceContext";
import { ANALYTICS_EVENTS } from "@/lib/analytics/catalog";
import { trackResult, trackUi } from "@/lib/analytics/amplitude";
import { tokenPurchaseConfig } from "@/lib/token-purchase-config";

// Define interfaces
interface CurrencyRate {
  usdToToken: number;
  rubToUsd?: number;
  kztToUsd?: number;
}

interface CurrencyOption {
  code: string;
  symbol: string;
  flag: string;
}

interface TokenPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseFinish: () => void;
  currencyRate?: CurrencyRate;
}

// Payment widget callback types
interface PaymentOptions {
  [key: string]: unknown;
}

interface PaymentResult {
  status?: string;
  transactionId?: string;
  [key: string]: unknown;
}

interface PaymentCallbacks {
  onSuccess?: (options?: PaymentOptions) => void;
  onFail?: (reason?: string | Error, options?: PaymentOptions) => void;
  onComplete?: (paymentResult?: PaymentResult, options?: PaymentOptions) => void;
}

// Constants
const TOKEN_COUNTS = [100, 500, 1000, 2000, 5000, 10000];

const DISCOUNTS: Record<number, number> = {
  100: 0,
  500: 6,
  1000: 11,
  2000: 19,
  5000: 31,
  10000: 39,
};

// Utility functions
const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatPrice = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2);
};

// Mock hook for country detection (will be replaced with real implementation)
const useCountryDetection = () => {
  return {
    countryCode: "US", // Mock country code
    isLoading: false,
  };
};

export const TokenPurchaseModal: React.FC<TokenPurchaseModalProps> = ({
  isOpen,
  onClose,
  onPurchaseFinish,
  currencyRate: currencyRateProp,
}) => {
  const { isB2B, b2bTokenCount, b2bPriceKzt } = tokenPurchaseConfig;
  const [selectedCount, setSelectedCount] = useState(() =>
    isB2B ? b2bTokenCount : 1000
  );
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState(() =>
    isB2B ? "KZT" : "USD"
  );
  const wasOpenRef = useRef(false);
  const { balances, refreshBalance } = useBalance();
  const { countryCode, isLoading: isCountryLoading } = useCountryDetection();

  // Use currency rate from balance context or fallback to prop
  // Priority: balances.currencyRate > prop > default fallback
  const currencyRate = balances?.currencyRate ?? currencyRateProp ?? DEFAULT_CURRENCY_RATE;

  const tokenCountOptions = useMemo(() => {
    if (!isB2B) {
      return TOKEN_COUNTS;
    }

    return [b2bTokenCount, ...TOKEN_COUNTS.slice(1)];
  }, [b2bTokenCount, isB2B]);

  const discount = isB2B ? 0 : DISCOUNTS[selectedCount] || 0;
  const discountMultiplier = (100 - discount) / 100;

  // Function to determine default currency by country
  const getDefaultCurrencyByCountry = (countryCode: string | null): string => {
    if (!countryCode) {
      return "USD";
    }

    let result: string;
    switch (countryCode) {
      case "RU":
        result = "RUB"; // Россия
        break;
      case "KZ":
        result = "KZT"; // Казахстан
        break;
      case "OTHER":
      default:
        result = "USD"; // Все остальные страны
        break;
    }

    return result;
  };

  // Memoize preferred currency to avoid recalculation on every currencyRate change
  const preferredCurrency = useMemo(() => {
    return getDefaultCurrencyByCountry(countryCode);
  }, [countryCode]);

  // Define available currencies
  const availableCurrencies = useMemo(() => {
    if (isB2B) {
      const kztCurrency = { code: "KZT", symbol: "₸", flag: "🇰🇿" };
      return { list: [kztCurrency], default: kztCurrency.code };
    }

    const currencies: CurrencyOption[] = [];
    // USD is always available for Stripe payments - first in the list
    currencies.push({ code: "USD", symbol: "$", flag: "🇺🇸" });
    if (currencyRate?.kztToUsd) {
      currencies.push({ code: "KZT", symbol: "₸", flag: "🇰🇿" });
    }
    if (currencyRate?.rubToUsd) {
      currencies.push({ code: "RUB", symbol: "₽", flag: "🇷🇺" });
    }

    // Проверяем, доступна ли предпочтительная валюта
    const defaultCurrency =
      currencies.find((c) => c.code === preferredCurrency)?.code || "USD";

    return { list: currencies, default: defaultCurrency };
  }, [currencyRate, isB2B, preferredCurrency]);

  const currencyInputs = `${isB2B}:${b2bTokenCount}:${isCountryLoading}:${availableCurrencies.default}`;
  const [previousCurrencyInputs, setPreviousCurrencyInputs] = useState<string | null>(null);
  if (previousCurrencyInputs !== currencyInputs) {
    setPreviousCurrencyInputs(currencyInputs);
    (() => {
    if (isB2B) {
      setSelectedCount(b2bTokenCount);
      setSelectedCurrencyCode("KZT");
      return;
    }

    // Update selected currency code if default changes (e.g., after country detection)
    if (!isCountryLoading && availableCurrencies.default) {
      setSelectedCurrencyCode(availableCurrencies.default);
    }
  })();
  }


  const handleBuyTokens = async (
    tokenCount: number,
    amount: number,
    currency: string
  ) => {
    trackUi(ANALYTICS_EVENTS.UI_CHECKOUT_STARTED, {
      token_count: tokenCount,
      amount,
      currency,
      selected_currency_code: selectedCurrencyCode,
      discount_percent: DISCOUNTS[tokenCount] ?? 0,
    });
    onPurchaseFinish();
    YMTracker.trackPaymentInitiate(amount);
    const currentUser = await authService.getCurrentUser();

    const paymentCallbacks: PaymentCallbacks = {
      onSuccess: () => {
        refreshBalance();
        YMTracker.trackPaymentSuccess(amount);
        trackResult(ANALYTICS_EVENTS.RESULT_CHECKOUT_SUCCESS, {
          token_count: tokenCount,
          amount,
          currency,
          selected_currency_code: selectedCurrencyCode,
        });
      },
      onFail: () => {
        YMTracker.trackPaymentCancel();
        trackResult(ANALYTICS_EVENTS.RESULT_CHECKOUT_FAILED, {
          token_count: tokenCount,
          amount,
          currency,
          selected_currency_code: selectedCurrencyCode,
          error_code: "payment_cancelled_or_failed",
        });
      },
      onComplete: () => {
        // Payment completed
      },
    };

    try {
      if (selectedCurrencyCode === "RUB") {
        const paymentWidget = new YooKassaPaymentWidget();
        await paymentWidget.initiatePayment(
          tokenCount,
          amount,
          currency,
          currentUser!.uid,
          currentUser?.email ?? "",
          paymentCallbacks
        );
      } else if (selectedCurrencyCode === "USD") {
        const paymentWidget = new StripePaymentWidget();
        await paymentWidget.initiatePayment(
          tokenCount,
          amount,
          currency,
          currentUser!.uid,
          currentUser?.email ?? "",
          paymentCallbacks
        );
      } else {
        const paymentWidget = new TiptopPaymentWidget();
        paymentWidget.initiatePayment(
          tokenCount,
          amount,
          currency,
          currentUser!.uid,
          currentUser?.email ?? "",
          paymentCallbacks
        );
      }
    } catch (error) {
      trackResult(ANALYTICS_EVENTS.RESULT_CHECKOUT_FAILED, {
        token_count: tokenCount,
        amount,
        currency,
        selected_currency_code: selectedCurrencyCode,
        error_code:
          error instanceof Error ? error.name : "payment_widget_initiation_failed",
      });
      throw error;
    }
  };

  const calculatePrice = (tokens: number) => {
    const usdPrice = tokens / currencyRate.usdToToken;
    // Calculate price based on selected currency
    const selectedCurrency = availableCurrencies.list.find(
      (c) => c.code === selectedCurrencyCode
    );
    if (!selectedCurrency) return 0; // Should not happen if logic is correct

    if (selectedCurrency.code === "RUB") {
      return usdPrice / currencyRate.rubToUsd!;
    } else if (selectedCurrency.code === "KZT") {
      return usdPrice / currencyRate.kztToUsd!;
    } else if (selectedCurrency.code === "USD") {
      return usdPrice; // Price is already in USD
    }
    return 0; // Fallback
  };

  const getCurrencySymbol = () => {
    const selectedCurrency = availableCurrencies.list.find(
      (c) => c.code === selectedCurrencyCode
    );
    return selectedCurrency?.symbol || "";
  };

  const baseTokenPrice = isB2B
    ? b2bPriceKzt / b2bTokenCount
    : selectedCurrencyCode === "RUB"
      ? 1 / currencyRate.usdToToken / currencyRate.rubToUsd!
      : selectedCurrencyCode === "KZT"
      ? 1 / currencyRate.usdToToken / currencyRate.kztToUsd!
      : selectedCurrencyCode === "USD"
      ? 1 / currencyRate.usdToToken
      : 0;
  const discountedTokenPrice = baseTokenPrice * discountMultiplier;

  const originalTokenPriceStr = `${formatPrice(
    baseTokenPrice
  )}${getCurrencySymbol().trim()}`;
  const discountedTokenPriceStr = `${formatPrice(
    discountedTokenPrice
  )}${getCurrencySymbol()}`;

  const originalTotalValue = isB2B ? b2bPriceKzt : calculatePrice(selectedCount);
  const discountedTotalValue = isB2B
    ? b2bPriceKzt
    : originalTotalValue * discountMultiplier;

  // Round to whole numbers for KZT and RUB, keep 2 decimals for USD
  const formatTotalValue = (value: number) => {
    if (selectedCurrencyCode === "KZT" || selectedCurrencyCode === "RUB") {
      return Math.round(value);
    }
    return parseFloat(value.toFixed(2));
  };

  const originalTotalStr = `${formatNumber(
    formatTotalValue(originalTotalValue)
  )}${getCurrencySymbol()}`;
  const discountedTotalStr = `${formatNumber(
    formatTotalValue(discountedTotalValue)
  )}${getCurrencySymbol()}`;

  const getCurrency = () => selectedCurrencyCode;

  const handleClose = () => {
    YMTracker.trackPaymentCancel();
    trackUi(ANALYTICS_EVENTS.UI_TOKEN_PURCHASE_MODAL_CLOSED, {
      selected_currency_code: selectedCurrencyCode,
      selected_token_count: selectedCount,
    });
    onClose();
  };

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      YMTracker.trackPaymentModalOpen();
      trackUi(ANALYTICS_EVENTS.UI_TOKEN_PURCHASE_MODAL_OPENED, {
        selected_currency_code: selectedCurrencyCode,
        selected_token_count: selectedCount,
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, selectedCount, selectedCurrencyCode]);

  // Don't render anything if modal is not open
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all duration-300 ease-in-out animate-in fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="max-w-md w-full mx-4 transition-all duration-300 ease-in-out animate-in zoom-in-95 slide-in-from-bottom-2">
        <div
          className="bg-card dark:bg-card relative p-0 rounded-3xl"
          style={{
            // backgroundColor: "var(--background)",
            // borderRadius: "22px",
            transform: "translate(0, -20%)",
          }}
        >
          {/* Inset Container */}
          <div
            className="bg-[var(--bw)] dark:bg-muted relative border rounded-3xl p-5 shadow-2xl"
            // style={{
            //   border: "0.75px solid var(--border-secondary)",
            //   padding: "20px 20px 20px",
            //   //   backgroundColor: "var(--background-secondary)",
            //   borderRadius: "22px",
            // }}
          >
            {/* Header with title and close button */}
            <div className="flex justify-between items-center pb-2">
              <h2
                className="text-2xl font-semibold m-0"
                style={{ color: "var(--text-secondary)" }}
              >
                Buy More Tokens
              </h2>
              <Button
                onClick={handleClose}
                variant="ghost"
                size="icon"
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Description */}
            <p className="text-sm text-muted-foreground mb-4">
              Choose the number of tokens you&apos;d like to purchase.
              <br />
              These tokens can be used anytime and do not expire.
            </p>

            {/* Divider */}
            <div
              className="w-full my-5"
              style={{
                height: "0.5px",
                backgroundColor: "var(--border)",
              }}
            />

            {/* Token selection buttons */}
            <div className="flex flex-wrap gap-2 pb-2" style={{ gap: "8px" }}>
              {tokenCountOptions.map((amount, index) => {
                const isSelected = isB2B
                  ? index === 0
                  : selectedCount === amount;

                return (
                  <Button
                    key={`${amount}-${index}`}
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    disabled={isB2B}
                    onClick={() => {
                      if (!isB2B) {
                        setSelectedCount(amount);
                      }
                    }}
                    style={{
                      backgroundColor: isSelected ? undefined : "var(--card)",
                      borderColor: isSelected
                        ? "var(--primary)"
                        : "transparent",
                    }}
                    className={`flex-1 min-w-24 h-10 text-sm font-medium rounded-lg border ${
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "hover:bg-muted hover:!border-primary"
                    }`}
                  >
                    {formatNumber(amount)}
                  </Button>
                );
              })}
            </div>

            {/* Token info */}
            <p className="text-xs text-muted-foreground/80 mt-2">
              {formatNumber(selectedCount)} tokens = ~
              {formatNumber(selectedCount / 100)} reports
            </p>

            {/* Country loading indicator */}
            {!isB2B && isCountryLoading && (
              <div
                className="flex justify-center items-center mt-3 py-2 px-2 rounded-lg text-sm"
                style={{
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border-secondary)",
                  borderRadius: "8px",
                }}
              >
                Detecting your location...
              </div>
            )}
          </div>

          {/* Payment section */}
          <div
            className="flex flex-col p-5 gap-4"
            // style={{
            //   padding: "22px",
            //   gap: "12px",
            // }}
          >
            {/* Combined payment info */}
            <div className="flex flex-col" style={{ gap: "6px" }}>
              {/* Token rate container */}
              <div
                className="inline-flex items-center justify-start"
                style={{ gap: "4px" }}
              >
                <div
                  className="bg-[var(--bw)] dark:bg-background flex items-center rounded-full px-2 py-1.5"
                  //   style={{
                  //     gap: "6px",
                  //     backgroundColor: "var(--background-secondary)",
                  //     borderRadius: "80px",
                  //     height: "20px",
                  //     padding: "1px 6px",
                  //   }}
                >
                  <span
                    className="text-xs text-muted-foreground/80 font-normal"
                    // style={{
                    //   color: "var(--text-disabled)",
                    //   fontSize: "12px",
                    //   fontWeight: "400",
                    //   lineHeight: "130%",
                    // }}
                  >
                    1 token ={" "}
                    {discount > 0 ? (
                      <>
                        <span
                          className="font-semibold"
                          style={{
                            color: "var(--brand)",
                            fontWeight: "600",
                          }}
                        >
                          {discountedTokenPriceStr}
                        </span>{" "}
                        <span
                          className="text-xs text-muted-foreground/80 relative inline-block"
                          //   style={{
                          //     color: "var(--text-disabled)",
                          //     position: "relative",
                          //     display: "inline-block",
                          //   }}
                        >
                          {originalTokenPriceStr}
                          <span
                            style={{
                              content: '""',
                              position: "absolute",
                              left: "-1px",
                              top: "50%",
                              right: "-1px",
                              borderTop: "2px solid var(--brand)",
                              transform: "rotate(-45deg)",
                              transformOrigin: "center",
                              pointerEvents: "none",
                            }}
                          />
                        </span>
                      </>
                    ) : (
                      <span
                        className="font-semibold"
                        style={{
                          color: "var(--text)",
                          fontWeight: "600",
                        }}
                      >
                        {discountedTokenPriceStr}
                      </span>
                    )}
                  </span>
                </div>
                {discount > 0 && (
                  <span
                    className="inline-flex justify-center items-center text-xs font-medium px-1.25 py-0.75 rounded-br-2xl rounded-tl-2xl "
                    style={{
                      //   color: "var(--text-on-accent)",
                      backgroundColor: "var(--brand)",
                      //   height: "20px",
                      //   fontSize: "10px",
                      //   padding: "0px 3px",
                      //   marginTop: "1px",
                      //   borderRadius: "12px 2px 12px 2px",
                    }}
                  >
                    -{discount}%
                  </span>
                )}
              </div>

              {/* Total info container */}
              <div className="flex justify-between items-end w-full">
                <div className="flex flex-row items-end gap-2">
                  <span
                    className="text-2xl font-semibold"
                    // style={{
                    //   color: "var(--text)",
                    //   fontSize: "20px",
                    //   fontWeight: "590",
                    //   lineHeight: "130%",
                    //   height: "29px",
                    //   opacity: "0.9",
                    // }}
                  >
                    Total: {discountedTotalStr}
                  </span>
                  {discount > 0 && (
                    <span
                      className="text-xs text-muted-foreground/80 line-through pb-1"
                      //   style={{
                      //     color: "var(--text-disabled)",
                      //     fontSize: "12px",
                      //     fontWeight: "500",
                      //     paddingLeft: "6px",
                      //   }}
                    >
                      {originalTotalStr}
                    </span>
                  )}
                </div>

                {/* Currency selector */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      disabled={isB2B}
                      className="flex items-center px-2 py-1 text-sm rounded-lg border transition-colors"
                      style={{
                        backgroundColor: "var(--background-secondary)",
                        border: "1px solid var(--transparent-background)",
                        borderRadius: "8px",
                        padding: "0 8px",
                        height: "32px",
                        cursor: isB2B ? "not-allowed" : "pointer",
                        opacity: isB2B ? 0.7 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (isB2B) {
                          return;
                        }
                        e.currentTarget.style.borderColor =
                          "var(--border-secondary)";
                      }}
                      onMouseLeave={(e) => {
                        if (isB2B) {
                          return;
                        }
                        e.currentTarget.style.borderColor =
                          "var(--transparent-background)";
                      }}
                    >
                      <span className="mr-1">
                        {
                          availableCurrencies.list.find(
                            (c) => c.code === selectedCurrencyCode
                          )?.flag
                        }
                      </span>
                      <span>{selectedCurrencyCode}</span>
                      {!isB2B && <ChevronDown className="ml-1 h-3 w-3" />}
                    </button>
                  </DropdownMenuTrigger>
                  {!isB2B && (
                    <DropdownMenuContent align="end">
                      {availableCurrencies.list.map((currency) => (
                        <DropdownMenuItem
                          key={currency.code}
                          onClick={() =>
                            setSelectedCurrencyCode(currency.code)
                          }
                          className="flex items-center gap-2"
                        >
                          <span>{currency.flag}</span>
                          <span>{currency.code}</span>
                          <span className="text-muted-foreground">
                            {currency.symbol}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  )}
                </DropdownMenu>
              </div>
            </div>

            {/* Purchase button */}
            <Button
              onClick={() =>
                handleBuyTokens(
                  selectedCount,
                  discountedTotalValue,
                  getCurrency()
                )
              }
              className="w-full h-10"
            >
              Proceed to Payment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenPurchaseModal;

const DEFAULT_CURRENCY_RATE = { usdToToken: 10, kztToUsd: 0.0022, rubToUsd: 0.011 };
